import { defineField, defineType } from 'sanity'
import { strongEmLink } from '../../data/richText'
import { PageBlockTypes } from '../pageBlocks'
import getPageInfo, { seoGroup } from '../objects/pageInfo'
import getFitmentGuidePageInfo, { fitmentGuideSeoGroup } from '../objects/fitmentGuidePageInfo'
import getGalleryPageInfo, { gallerySeoGroup } from '../objects/galleryPageInfo'

export default defineType({
  name: 'model',
  title: 'Models',
  type: 'document',
  orderings: [
    {
      title: 'Name A-Z',
      name: 'nameAsc',
      by: [{field: 'name', direction: 'asc'}],
    },
  ],
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    seoGroup,
    fitmentGuideSeoGroup,
    gallerySeoGroup,
  ],
  fields: [
    defineField({
      name: 'name',
      title: 'Model Name',
      type: 'string',
      validation: (rule) => rule.required(),
      group: 'content',
    }),
    defineField({
      name: 'isHidden',
      title: 'Hidden?',
      description:
        'If set to hidden it will not show in vehicle selector, galleries, fitment guides, navigation, or allow direct page access.',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      name: 'description',
      title: 'Fitment Guide Description',
      description: 'Shows on fitment guides index pages',
      type: 'array',
      of: strongEmLink,
      group: 'content',
    }),
    defineField({
      name: 'vehicleIndexDescription',
      title: 'Vehicle Index Page Description',
      description: 'Shows on vehicle index pages',
      type: 'array',
      of: strongEmLink,
      group: 'content',
    }),
    defineField({
      name: 'photo',
      title: 'Photo',
      type: 'image',
      options: {
        hotspot: true,
      },
      description: 'Used for thumbnails on the /vehicles/:make page.',
      hidden: ({ parent }) => parent?.isHidden,
      group: 'content',
    }),
    defineField({
      name: 'generations',
      title: 'Generations',
      type: 'array',
      of: [
        {
          type: 'reference',
          to: [{ type: 'generation' }],
        },
      ],
      group: 'content',
    }),
    defineField({
      title: 'Enable Landing Page?',
      name: 'isLanding',
      type: 'boolean',
      initialValue: false,
      hidden: ({ parent }) => parent?.isHidden,
      group: 'content',
    }),
    defineField({
      name: 'title',
      title: 'Title',
      type: 'string',
      description:
        'Used as h1 on /vehicles/:make/:model page. Defaults to ":Model Vehicles"',
      hidden: ({ parent }) => !parent?.isLanding,
      group: 'content',
    }),
    defineField({
      name: 'subtitle',
      title: 'Sub Title',
      type: 'string',
      description:
        'Used as h2 on /vehicles/:make page. Defaults to "Select your Generation"',
      hidden: ({ parent }) => !parent?.isLanding,
      group: 'content',
    }),
    defineField({
      title: 'Page blocks',
      name: 'pageBlocks',
      type: 'array',
      of: PageBlockTypes,
      group: 'content',
    }),
    defineField({
      title: 'Override Generation Landing Page with this one.',
      description:
        "If this setting is on, Generation page will use Model's `Page Info` instead. Eg. /vehicles/tesla/model-3 instead of /vehicles/tesla/model-3/model-3. (Will not work if there is more then one generation). Currently configured makes are Tesla and Mazda.",
      name: 'overrideGeneration',
      type: 'boolean',
      initialValue: false,
      hidden: ({ parent }) => parent?.isHidden || !parent?.isLanding,
      group: 'content',
    }),
    getPageInfo({ path: 'vehicles' }),
    getFitmentGuidePageInfo({ path: 'fitment-guide' }),
    getGalleryPageInfo(),
  ],
  preview: {
    select: {
      title: 'name',
      hidden: 'isHidden',
    },
    prepare({ title, hidden }) {
      const subtitle = hidden ? 'HIDDEN' : ''
      return { title, subtitle }
    },
  },
})